import React, { useEffect, useMemo, useState } from "react";
import { Collapse, Input, Select, Switch, message } from "antd";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  addViewToDashboard,
  createFormula,
  executeFormula,
  getFormula,
  getFormulaVariables,
  getFormulaVocabulary,
  getResultViews,
  saveFormula,
  updateFormula,
  validateFormula,
} from "./formulaBuilder.api";
import type {
  FormulaOutputType,
  FormulaPayload,
  FormulaToken,
  FormulaVariable,
  FormulaVariablesResponse,
  FormulaVocabulary,
  ResultViewKey,
  ResultViewsMap,
  VariableToken,
} from "./formulaBuilder.types";
import {
  GRAIN_LABELS,
  OUTPUT_TYPE_LABELS,
  RESULT_VIEW_KEYS,
  buildExpression,
  tokenLabel,
  variableToToken,
} from "./formulaBuilder.utils";
import FormulaResultSection from "./FormulaResultSection";

const FALLBACK_GRAIN_OPTIONS = Object.entries(GRAIN_LABELS).map(
  ([value, label]) => ({ value, label }),
);
const FALLBACK_OUTPUT_TYPES: FormulaOutputType[] = [
  "score",
  "percent",
  "index",
  "count",
  "ratio",
];

type ActiveFunction = {
  name: "mean" | "median" | "mode" | "std_dev";
  args: VariableToken[];
};

type Props = {
  formulaId?: string;
  onCancel: () => void;
  onSaved: () => void;
};

const FormulaBuilderForm: React.FC<Props> = ({
  formulaId,
  onCancel,
  onSaved,
}) => {
  const { t } = useTranslation("formulaBuilder");
  const { id: projectId } = useParams();

  const [currentFormulaId, setCurrentFormulaId] = useState<string | undefined>(
    formulaId,
  );
  const [formulaName, setFormulaName] = useState("");
  const [description, setDescription] = useState("");
  const [grain, setGrain] = useState<string>();
  const [outputType, setOutputType] = useState<FormulaOutputType>();
  const [targetValue, setTargetValue] = useState("");
  const [normalizeEnabled, setNormalizeEnabled] = useState(false);
  const [vocabulary, setVocabulary] = useState<FormulaVocabulary | null>(null);
  const [variables, setVariables] = useState<FormulaVariablesResponse>({
    promoted_data: [],
    promoted_codes: [],
    promoted_descriptors: [],
  });
  const [tokens, setTokens] = useState<FormulaToken[]>([]);
  const [activeFunction, setActiveFunction] = useState<ActiveFunction | null>(
    null,
  );
  const [literalInput, setLiteralInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [computedValue, setComputedValue] = useState<number | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [hasExecutedSuccessfully, setHasExecutedSuccessfully] =
    useState(false);
  const [resultViews, setResultViews] = useState<ResultViewsMap | null>(null);
  const [activeResultTab, setActiveResultTab] =
    useState<ResultViewKey>("value_vs_target");
  const [pinnedViews, setPinnedViews] = useState<Set<ResultViewKey>>(
    new Set(),
  );
  const [pinningView, setPinningView] = useState<ResultViewKey | null>(null);
  const [addingAll, setAddingAll] = useState(false);

  const expression = useMemo(() => buildExpression(tokens), [tokens]);

  const grainOptions = useMemo(
    () =>
      vocabulary?.grains?.length
        ? vocabulary.grains.map((g) => ({ value: g.value, label: g.label }))
        : FALLBACK_GRAIN_OPTIONS,
    [vocabulary],
  );

  const outputTypeOptions = useMemo(
    () =>
      (vocabulary?.output_types?.length
        ? vocabulary.output_types
        : FALLBACK_OUTPUT_TYPES
      ).map((t) => ({ value: t, label: OUTPUT_TYPE_LABELS[t] ?? t })),
    [vocabulary],
  );

  const tokenWarnings = useMemo(() => {
    const w: string[] = [];
    if (activeFunction !== null) {
      w.push(
        t("form.warnings.completeOrCancelFunction", { name: activeFunction.name }),
      );
    }
    for (let i = 0; i < tokens.length - 1; i++) {
      if (tokens[i].type !== "operator" && tokens[i + 1].type !== "operator") {
        w.push(t("form.warnings.missingOperator"));
        break;
      }
    }
    if (tokens.length > 0 && tokens[tokens.length - 1].type === "operator") {
      w.push(t("form.warnings.endsWithOperator"));
    }
    return w;
  }, [tokens, activeFunction, t]);

  const canSave =
    Boolean(projectId) &&
    formulaName.trim().length > 0 &&
    Boolean(grain) &&
    Boolean(outputType) &&
    Boolean(targetValue) &&
    tokens.length > 0 &&
    activeFunction === null &&
    tokenWarnings.length === 0;

  const resetResult = () => {
    setComputedValue(null);
    setErrors([]);
    setWarnings([]);
    setHasExecutedSuccessfully(false);
    setResultViews(null);
    setPinnedViews(new Set());
  };

  useEffect(() => {
    const loadVariables = async () => {
      if (!projectId) return;
      try {
        const response = await getFormulaVariables(projectId);
        if (response.type === "success") {
          setVariables(response.data);
        } else {
          message.error(t("form.errors.loadVariablesFailed"));
        }
      } catch {
        message.error(t("form.errors.loadVariablesFailed"));
      }
    };
    loadVariables();
  }, [projectId, t]);

  useEffect(() => {
    const loadVocabulary = async () => {
      if (!projectId) return;
      try {
        const response = await getFormulaVocabulary(projectId);
        if (response.type === "success") {
          setVocabulary(response.data);
        }
      } catch {
        // static fallback options are used when the vocabulary can't be loaded
      }
    };
    loadVocabulary();
  }, [projectId]);

  useEffect(() => {
    const loadFormula = async () => {
      if (!projectId || !formulaId) return;
      try {
        const response = await getFormula(projectId, formulaId);
        if (response.type !== "success") {
          message.error(t("form.errors.loadFormulaFailed"));
          return;
        }

        const formula = response.data;
        setCurrentFormulaId(formula.id);
        setFormulaName(formula.name ?? "");
        setDescription(formula.description ?? "");
        setGrain(formula.grain);
        setOutputType(formula.output_type);
        setTargetValue(
          formula.target_value != null ? String(formula.target_value) : "",
        );
        setNormalizeEnabled(Boolean(formula.normalize_enabled));
        setTokens(formula.token_json ?? []);
        setComputedValue(formula.last_result ?? null);

        if (formula.last_result != null) {
          setHasExecutedSuccessfully(true);
          const viewsResponse = await getResultViews(projectId, formulaId);
          if (viewsResponse.type === "success") {
            setResultViews(viewsResponse.data.views);
            setPinnedViews(new Set(viewsResponse.data.on_dashboard ?? []));
          }
        }
      } catch {
        message.error(t("form.errors.loadFormulaFailed"));
      }
    };
    loadFormula();
  }, [projectId, formulaId, t]);

  const addVariable = (variable: FormulaVariable) => {
    const token = variableToToken(variable);
    if (activeFunction) {
      setActiveFunction((prev) =>
        prev ? { ...prev, args: [...prev.args, token] } : null,
      );
    } else {
      setTokens((prev) => [...prev, token]);
    }
    resetResult();
  };

  const addOperator = (op: "+" | "-" | "*" | "/" | "%") => {
    setTokens((prev) => [...prev, { type: "operator", op }]);
    resetResult();
  };

  const startFunction = (name: "mean" | "median" | "mode" | "std_dev") => {
    if (activeFunction) finalizeFunction();
    setActiveFunction({ name, args: [] });
    resetResult();
  };

  const finalizeFunction = () => {
    if (!activeFunction || activeFunction.args.length === 0) {
      setActiveFunction(null);
      return;
    }
    setTokens((prev) => [
      ...prev,
      {
        type: "function",
        name: activeFunction.name,
        args: activeFunction.args,
      },
    ]);
    setActiveFunction(null);
    resetResult();
  };

  const cancelFunction = () => setActiveFunction(null);

  const removeFunctionArg = (index: number) => {
    setActiveFunction((prev) =>
      prev ? { ...prev, args: prev.args.filter((_, i) => i !== index) } : null,
    );
  };

  const addLiteralToken = () => {
    const num = parseFloat(literalInput);
    if (isNaN(num)) return;
    setTokens((prev) => [...prev, { type: "literal", value: num }]);
    setLiteralInput("");
    resetResult();
  };

  const removeToken = (index: number) => {
    setTokens((prev) => prev.filter((_, i) => i !== index));
    resetResult();
  };

  const buildPayload = (): FormulaPayload => ({
    name: formulaName,
    description,
    grain: grain as FormulaPayload["grain"],
    grain_join_key: null,
    result_shape: vocabulary?.defaults.result_shape ?? "aggregate",
    aggregate_function: vocabulary?.defaults.aggregate_function ?? "mean",
    normalize_enabled: normalizeEnabled,
    normalize_target_scale: "0_1",
    token_json: tokens,
    expression,
    output_type: outputType as FormulaOutputType,
    target_value: targetValue === "" ? null : Number(targetValue),
    target_direction: "higher_is_better",
    null_handling: "skip",
    apply_row_weights: vocabulary?.defaults.apply_row_weights ?? true,
    in_library: false,
    is_named_result: false,
  });

  const ensureFormulaSaved = async () => {
    if (!projectId) throw new Error(t("form.errors.projectIdMissing"));
    if (!canSave) throw new Error(t("form.errors.fillRequiredFields"));

    const payload = buildPayload();

    if (currentFormulaId) {
      const response = await updateFormula(
        projectId,
        currentFormulaId,
        payload,
      );
      if (response.type !== "success")
        throw new Error(t("form.errors.updateFailed"));
      return response.data.id ?? currentFormulaId;
    }

    const response = await createFormula(projectId, payload);
    if (response.type !== "success")
      throw new Error(t("form.errors.createFailed"));
    const newFormulaId = response.data.id;
    setCurrentFormulaId(newFormulaId);
    return newFormulaId;
  };

  const handleValidate = async () => {
    if (!projectId) return;
    setVerifying(true);
    try {
      const savedFormulaId = await ensureFormulaSaved();
      const response = await validateFormula(projectId, savedFormulaId);
      if (response.type === "success") {
        setErrors(response.data.errors ?? []);
        setWarnings(response.data.warnings ?? []);
        if (response.data.is_valid) {
          message.success(t("form.messages.formulaValid"));
        } else {
          message.error(t("form.messages.formulaInvalid"));
        }
      } else {
        message.error(t("form.errors.validateFailed"));
      }
    } catch (error: any) {
      message.error(error.message || t("form.errors.validateFailed"));
    } finally {
      setVerifying(false);
    }
  };

  const handleExecute = async () => {
    if (!projectId) return;
    setExecuting(true);
    try {
      const savedFormulaId = await ensureFormulaSaved();
      const response = await executeFormula(projectId, savedFormulaId);
      if (response.type === "success") {
        setErrors(response.data.errors ?? []);
        setWarnings(response.data.warnings ?? []);
        setComputedValue(response.data.result ?? null);
        if (response.data.is_valid) {
          setHasExecutedSuccessfully(true);
          message.success(t("form.messages.executeSuccess"));
        } else {
          message.error(t("form.messages.executeFailedStatus"));
        }
      } else {
        message.error(t("form.errors.executeFailed"));
      }
    } catch (error: any) {
      message.error(error.message || t("form.errors.executeFailed"));
    } finally {
      setExecuting(false);
    }
  };

  const handleSaveFormula = async () => {
    if (!projectId) return;
    setSaving(true);
    try {
      const savedFormulaId = await ensureFormulaSaved();
      const response = await saveFormula(projectId, savedFormulaId);
      if (response.type === "success") {
        setErrors(response.data.errors ?? []);
        setWarnings(response.data.warnings ?? []);
        setComputedValue(response.data.result ?? null);
        setResultViews(response.data.views ?? null);
        setPinnedViews(new Set());
        setActiveResultTab("value_vs_target");
        if (response.data.is_valid) {
          message.success(t("form.messages.saveSuccess"));
        } else {
          message.error(t("form.messages.saveFailedStatus"));
        }
      } else {
        message.error(t("form.errors.saveFailed"));
      }
    } catch (error: any) {
      message.error(error.message || t("form.errors.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleAddToDashboard = async (view: ResultViewKey) => {
    if (!projectId || !currentFormulaId) return;
    setPinningView(view);
    try {
      const response = await addViewToDashboard(
        projectId,
        currentFormulaId,
        view,
        { title: formulaName },
      );
      if (response.type === "success") {
        setPinnedViews((prev) => new Set(prev).add(view));
      } else {
        message.error(t("form.errors.addToDashboardFailed"));
      }
    } catch {
      message.error(t("form.errors.addToDashboardFailed"));
    } finally {
      setPinningView(null);
    }
  };

  const handleAddAllViews = async () => {
    if (!projectId || !currentFormulaId) return;
    setAddingAll(true);
    try {
      const results = await Promise.all(
        RESULT_VIEW_KEYS.map((view) =>
          addViewToDashboard(projectId, currentFormulaId, view, {
            title: formulaName,
          }),
        ),
      );
      const succeeded = RESULT_VIEW_KEYS.filter(
        (_, index) => results[index].type === "success",
      );
      if (succeeded.length) {
        setPinnedViews((prev) => {
          const next = new Set(prev);
          succeeded.forEach((view) => next.add(view));
          return next;
        });
        message.success(t("form.messages.addViewSuccess", { count: succeeded.length }));
      }
      if (succeeded.length < RESULT_VIEW_KEYS.length) {
        message.error(t("form.messages.addViewPartialFailed"));
      }
    } finally {
      setAddingAll(false);
    }
  };

  return (
    <div className="formula-form-section">
      <h1>{formulaId ? t("form.editTitle") : t("form.addTitle")}</h1>

      <div className="formula-top-fields">
        <div>
          <label>{t("form.nameLabel")}</label>
          <Input
            placeholder={t("form.namePlaceholder")}
            value={formulaName}
            onChange={(e) => setFormulaName(e.target.value)}
          />
        </div>

        <div>
          <label>{t("form.grainLabel")}</label>
          <Select
            placeholder={t("form.grainPlaceholder")}
            value={grain}
            onChange={(value) => {
              setGrain(value);
              resetResult();
            }}
            style={{ width: "100%" }}
            options={grainOptions}
          />
        </div>

        <div style={{ gridColumn: "1 / -1" }}>
          <label>{t("form.descriptionLabel")}</label>
          <Input.TextArea
            placeholder={t("form.descriptionPlaceholder")}
            value={description}
            rows={2}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
      </div>

      <div className="formula-builder-layout">
        <div className="formula-main-card">
          <h2>{t("form.builderTitle")}</h2>

          <label>{t("form.variableSelectionLabel")}</label>

          <Collapse
            defaultActiveKey={["promoted_codes", "promoted_descriptors"]}
            items={[
              {
                key: "promoted_data",
                label: t("form.collapse.promotedData"),
                children: (
                  <VariableGrid
                    variables={variables.promoted_data}
                    onSelect={addVariable}
                  />
                ),
              },
              {
                key: "promoted_codes",
                label: t("form.collapse.promotedCodes"),
                children: (
                  <VariableGrid
                    variables={variables.promoted_codes}
                    onSelect={addVariable}
                  />
                ),
              },
              {
                key: "promoted_descriptors",
                label: t("form.collapse.promotedDescriptors"),
                children: (
                  <VariableGrid
                    variables={variables.promoted_descriptors}
                    onSelect={addVariable}
                  />
                ),
              },
            ]}
          />

          <div className="formula-editor">
            <div className="formula-editor-header">
              <label>{t("form.formulaLabel")}</label>
              <button
                type="button"
                onClick={handleValidate}
                disabled={!canSave || verifying}
              >
                {verifying ? t("form.verifying") : t("form.verifyFormula")}
              </button>
            </div>

            <div className="formula-token-box">
              {tokens.map((token, index) => (
                <span
                  key={`${token.type}-${index}`}
                  className={`formula-token formula-token-${token.type}`}
                >
                  {tokenLabel(token)}
                  <button
                    type="button"
                    className="formula-token-remove"
                    onClick={() => removeToken(index)}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>

            {activeFunction && (
              <div className="formula-function-builder">
                <span className="formula-function-label">
                  {t("form.building")} <strong>{activeFunction.name}(</strong>
                </span>
                {activeFunction.args.map((arg, i) => (
                  <span
                    key={i}
                    className="formula-token formula-token-variable"
                  >
                    {arg.label}
                    <button
                      type="button"
                      className="formula-token-remove"
                      onClick={() => removeFunctionArg(i)}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <span className="formula-function-label">
                  <strong>)</strong>
                </span>
                <button
                  type="button"
                  className="formula-function-add-btn"
                  onClick={finalizeFunction}
                  disabled={activeFunction.args.length === 0}
                >
                  {t("form.addToFormula")}
                </button>
                <button
                  type="button"
                  className="formula-function-cancel-btn"
                  onClick={cancelFunction}
                >
                  {t("form.cancel")}
                </button>
              </div>
            )}

            {tokenWarnings.length > 0 && (
              <div className="formula-token-warnings">
                {tokenWarnings.map((warning, index) => (
                  <p key={index} className="formula-token-warning-msg">
                    ⚠ {warning}
                  </p>
                ))}
              </div>
            )}

            <Input.TextArea value={expression} readOnly rows={3} />
          </div>

          {errors.length > 0 && (
            <div className="formula-errors">
              {errors.map((error, index) => (
                <p key={index}>{error}</p>
              ))}
            </div>
          )}

          {warnings.length > 0 && (
            <div className="formula-warnings">
              {warnings.map((warning, index) => (
                <p key={index}>{warning}</p>
              ))}
            </div>
          )}
        </div>

        <div className="formula-sidebar">
          <div className="formula-side-card">
            <div className="formula-switch-row">
              <span>{t("form.normalizeValues")}</span>
              <Switch
                checked={normalizeEnabled}
                onChange={(checked) => {
                  setNormalizeEnabled(checked);
                  resetResult();
                }}
              />
            </div>
            <p>{t("form.normalizeDescription")}</p>
          </div>

          <div className="formula-side-card">
            <h4>{t("form.mathToolbar")}</h4>
            <div className="formula-toolbar">
              {(["+", "-", "*", "%", "/"] as const).map((op) => (
                <button
                  key={op}
                  type="button"
                  disabled={!!activeFunction}
                  onClick={() => addOperator(op)}
                >
                  {op}
                </button>
              ))}
            </div>
          </div>

          <div className="formula-side-card">
            <h4>{t("form.functionToolbar")}</h4>
            {activeFunction && (
              <p className="formula-function-hint">
                Select variables to add inside{" "}
                <strong>{activeFunction.name}()</strong>
              </p>
            )}
            <div className="formula-toolbar">
              {(["mean", "median", "mode", "std_dev"] as const).map((fn) => (
                <button
                  key={fn}
                  type="button"
                  className={
                    activeFunction?.name === fn
                      ? "formula-toolbar-btn-active"
                      : ""
                  }
                  onClick={() => startFunction(fn)}
                >
                  {fn === "mean" && t("form.functions.mean")}
                  {fn === "median" && t("form.functions.median")}
                  {fn === "mode" && t("form.functions.mode")}
                  {fn === "std_dev" && t("form.functions.stdDev")}
                </button>
              ))}
            </div>
          </div>

          <div className="formula-side-card">
            <h4>{t("form.numberSection")}</h4>
            <div className="formula-literal-row">
              <Input
                type="number"
                value={literalInput}
                onChange={(e) => setLiteralInput(e.target.value)}
                placeholder={t("form.literalPlaceholder")}
                onPressEnter={addLiteralToken}
                disabled={!!activeFunction}
              />
              <button
                type="button"
                onClick={addLiteralToken}
                disabled={!literalInput || !!activeFunction}
              >
                {t("form.add")}
              </button>
            </div>
          </div>

          <div className="formula-side-card">
            <h4>{t("form.resultSection")}</h4>
            <label>{t("form.outputTypeLabel")}</label>
            <Select
              placeholder={t("form.outputTypePlaceholder")}
              value={outputType}
              onChange={(value) => {
                setOutputType(value);
                resetResult();
              }}
              style={{ width: "100%" }}
              options={outputTypeOptions}
            />
            <label style={{ marginTop: 12, display: "block" }}>{t("form.targetLabel")}</label>
            <Input
              type="number"
              placeholder={t("form.targetPlaceholder")}
              value={targetValue}
              onChange={(e) => {
                setTargetValue(e.target.value);
                resetResult();
              }}
            />
            <p className="muted formula-result-hint">
              {t("form.resultHint")}
            </p>
          </div>
        </div>
      </div>

      {computedValue !== null && (
        <div className="formula-computed-value">
          {t("form.computedValue")} <strong>{computedValue}</strong>
          {grain && (
            <span className="muted">
              {" "}
              · per {(GRAIN_LABELS[grain] ?? grain).replace(/^Per /, "")}
            </span>
          )}
          {outputType && (
            <span className="muted">
              {" "}
              · {OUTPUT_TYPE_LABELS[outputType] ?? outputType}
            </span>
          )}
        </div>
      )}

      <div className="formula-form-actions">
        <button type="button" onClick={onCancel}>
          {t("form.cancel")}
        </button>

        <button
          type="button"
          onClick={handleExecute}
          disabled={!canSave || executing}
        >
          {executing ? t("form.executing") : t("form.executeFormula")}
        </button>

        <button
          type="button"
          onClick={handleSaveFormula}
          disabled={!canSave || !hasExecutedSuccessfully || saving}
          title={
            !hasExecutedSuccessfully
              ? t("form.messages.executeBeforeSaving")
              : undefined
          }
        >
          {saving ? t("form.saving") : t("form.saveFormula")}
        </button>
      </div>

      <FormulaResultSection
        formulaName={formulaName}
        resultViews={resultViews}
        activeTab={activeResultTab}
        onTabChange={setActiveResultTab}
        pinnedViews={pinnedViews}
        pinningView={pinningView}
        addingAll={addingAll}
        onAddView={handleAddToDashboard}
        onAddAllViews={handleAddAllViews}
      />
    </div>
  );
};

type VariableGridProps = {
  variables: FormulaVariable[];
  onSelect: (variable: FormulaVariable) => void;
};

const VariableGrid: React.FC<VariableGridProps> = ({ variables, onSelect }) => {
  const { t } = useTranslation("formulaBuilder");

  if (!variables.length) {
    return <span className="muted">{t("form.noVariablesFound")}</span>;
  }

  return (
    <div className="formula-variable-grid">
      {variables.map((variable) => (
        <button
          key={variable.id}
          type="button"
          className="formula-variable-pill"
          onClick={() => onSelect(variable)}
        >
          <span>{variable.label}</span>
          <small>{variable.weight}</small>
        </button>
      ))}
    </div>
  );
};

export default FormulaBuilderForm;
